import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { getReadableTextColorFromBackgroundColorWithOpacity } from "@/lib/color-utils";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import type {
  OrganizationUserListItemDTO,
  OrganizationUserRoleSimpleDTO,
  OrganizationUserAdminDTO,
} from "@/orval/model";
import {
  adminOrganizationUserGetOrganizationUsers,
  getAdminOrganizationUserGetOrganizationUsersQueryKey,
  getAdminOrganizationUserGetOrganizationUsersQueryOptions,
} from "@/orval/admin-organization-user";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { EditOrganizationUserDialog } from "./-components/edit-organization-user-dialog";
import { useState } from "react";
import { PortalPageHeader } from "@/components/portal-page-header";
import { AccountStatusBadge } from "./-components/account-status-badge";

const roleColumn: ColumnDef<OrganizationUserListItemDTO, OrganizationUserRoleSimpleDTO[]> = {
  accessorKey: "userRoles",
  header: "Roles",
  cell: (context) => {
    return (
      <div className="flex flex-row gap-1">
        {context.getValue().map((role) => (
          <span
            key={role.id}
            className="px-3 py-1 rounded-full text-xs font-semibold text-foreground"
            style={{
              background: role.color,
              color: getReadableTextColorFromBackgroundColorWithOpacity(role.color, 0.75),
            }}
          >
            {role.roleName}
          </span>
        ))}
      </div>
    );
  },
};

const columns: ColumnDef<OrganizationUserListItemDTO, never>[] = [
  {
    accessorKey: "firstName",
    header: ({ column }) => <DataTableColumnHeader column={column} title="First Name" />,
    enableSorting: true,
  },
  {
    accessorKey: "lastName",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Name" />,
    enableSorting: true,
  },
  {
    accessorKey: "isAnonymous",
    header: "Account",
    cell: ({ row }) => <AccountStatusBadge isAnonymous={row.original.isAnonymous} />,
  },
  {
    accessorKey: "email",
    header: "Email",
    enableSorting: false,
  },
  {
    accessorKey: "phoneNumber",
    header: "Phone Number",
    enableSorting: false,
  },
  roleColumn,
];

const OrganizationAllMembersPage = () => {
  const { organizationId } = Route.useParams();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserAdminDTO | null>(null);

  const handleRowClick = (member: OrganizationUserListItemDTO) => {
    setSelectedUser({
      ...member,
      organizationId,
      email: member.email || null, // Ensure email is either string or null
      phoneNumber: member.phoneNumber || null, // Ensure phone is either string or null
      hasPendingTeacherInvite: false,
    });
    setDialogOpen(true);
  };

  const getUsers = adminOrganizationUserGetOrganizationUsers;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader className="mb-8" title="All Members" />

      <DataTable
        columns={columns}
        queryFn={(params, abortSignal) => getUsers(organizationId, params, abortSignal)}
        queryKeyFn={(params) => getAdminOrganizationUserGetOrganizationUsersQueryKey(organizationId, params)}
        onRowClick={handleRowClick}
        getRowIdFn={(row) => row.id.toString()}
      />

      {selectedUser && (
        <EditOrganizationUserDialog isOpen={isDialogOpen} onClose={() => setDialogOpen(false)} user={selectedUser} />
      )}
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/all-members")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);

    return ensureRouteQueryData(
      loaderContext,
      getAdminOrganizationUserGetOrganizationUsersQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions({ searchString }),
      ),
    );
  },
  component: OrganizationAllMembersPage,
});
